import { Loader2, FileX } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { QueryErrorBanner } from "./QueryErrorBanner";

/**
 * 居中 spinner,卡片内 / 全屏 loading 都通用。
 *
 * 默认 16 高度内联;`fullPage` 取全屏(min-h-[50vh])。
 *
 * V23 Session-2 Epic 5 — i18n: 默认走 t('actions.loading') + '…',caller 显式 text 覆盖。
 */
export function CenteredLoader({
  text,
  fullPage = false,
  className = "",
}: {
  text?: string;
  fullPage?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const displayText = text ?? `${t("actions.loading")}…`;
  return (
    <div
      role="status"
      className={
        "flex items-center justify-center gap-2 text-sm text-muted-foreground " +
        (fullPage ? "min-h-[50vh] " : "py-16 ") +
        className
      }
    >
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{displayText}</span>
    </div>
  );
}

/**
 * 空状态视图。query 成功但 data 为空时展示,统一文案 / icon。
 *
 * action 槽留给"创建第一条""上传"等 CTA 按钮。
 *
 * V23 Session-2 Epic 5 — i18n: 默认走 t('empty.noData'),caller 显式 title 覆盖。
 */
export function EmptyState({
  title,
  description,
  action,
  className = "",
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  const { t } = useTranslation();
  const displayTitle = title ?? t("empty.noData");
  return (
    <div
      className={
        "flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground " +
        className
      }
    >
      <FileX className="h-10 w-10 opacity-40" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground/80">{displayTitle}</p>
        {description && <p className="text-xs">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/**
 * useQuery 三态(loading / error / empty)统一渲染 wrapper。
 *
 * <p>之前各页面手写 `{isLoading ? <Loader2 ... /> : isError ? <ErrBanner ... /> : data.length === 0 ? <Empty /> : <Content />}` 模板代码,
 * 散落 94+ 处。改用本组件:
 *
 * ```tsx
 * <QueryState query={listQ} isEmpty={(d) => d.length === 0}>
 *   {(data) => <Table rows={data} />}
 * </QueryState>
 * ```
 *
 * @param query  必须有 isLoading / isError / refetch / data 字段(兼容 UseQueryResult)。
 * @param isEmpty  data ready 后用于判断是否空集。可选。
 * @param emptyTitle / emptyDescription / emptyAction  empty 状态文案。
 * @param errorMessage  覆盖默认错误提示。
 * @param loadingText  覆盖 loading 文案。
 * @param fullPageLoading  loading 是否全屏。
 * @param children  函数(data) => ReactNode,data 必然非 null。
 */
export function QueryState<T>({
  query,
  isEmpty,
  emptyTitle,
  emptyDescription,
  emptyAction,
  errorMessage,
  loadingText,
  fullPageLoading,
  children,
}: {
  query: {
    isLoading: boolean;
    isError: boolean;
    data: T | undefined;
    refetch: () => unknown;
  };
  isEmpty?: (data: T) => boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  errorMessage?: string;
  loadingText?: string;
  fullPageLoading?: boolean;
  children: (data: T) => ReactNode;
}) {
  if (query.isLoading) {
    return <CenteredLoader text={loadingText} fullPage={fullPageLoading} />;
  }
  if (query.isError) {
    return <QueryErrorBanner isError message={errorMessage} onRetry={() => query.refetch()} />;
  }
  const data = query.data;
  if (data === undefined || data === null) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  }
  if (isEmpty && isEmpty(data)) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  }
  return <>{children(data)}</>;
}
